import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import LiveNowBanner from '../../components/LiveNowBanner.tsx';
import { Button, Card, EmptyState, ErrorText, Input, Modal, Spinner, Tag, fmtDateTime } from '../../lib/ui.tsx';

interface Lesson {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  coachName?: string;
  isTrial: boolean;
  sharedSummary: string | null;
}

interface Slot {
  startsAt: string;
  endsAt: string;
}

interface WaitlistEntry {
  id: string;
  status: string;
  createdAt: string;
}

function fmtTime(d: string): string {
  return new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** G4 · ICS subscription URL. */
function IcsCard() {
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fetchUrl = useMutation({
    mutationFn: () => api<{ url: string }>('/api/scheduling/ics-url'),
    onSuccess: (res) => setUrl(res.url),
  });
  return (
    <Card title="Calendar feed (ICS)">
      {url ? (
        <div className="space-y-2">
          <Input readOnly value={url} onFocus={(e) => e.target.select()} />
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(url).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied ✅' : 'Copy URL'}
          </Button>
          <p className="text-xs text-slate-500">Subscribe from Google/Apple Calendar to see all lessons.</p>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => fetchUrl.mutate()} disabled={fetchUrl.isPending}>
          Get calendar URL
        </Button>
      )}
      <ErrorText error={fetchUrl.error} />
    </Card>
  );
}

export default function LessonsPage() {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rescheduleId, setRescheduleId] = useState<string | null>(null); // null → booking mode

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['student-lessons'] });
    void qc.invalidateQueries({ queryKey: ['slots'] });
    void qc.invalidateQueries({ queryKey: ['my-waitlist'] });
  };

  const { data: lessons, isLoading } = useQuery({
    queryKey: ['student-lessons'],
    // look back a few hours so a lesson that is mid-flight (or running late)
    // stays in the list with its Live-room link instead of vanishing at start
    queryFn: () =>
      api<Lesson[]>(
        `/api/scheduling/lessons?from=${encodeURIComponent(new Date(Date.now() - 3 * 3600 * 1000).toISOString())}`,
      ),
  });

  const { data: slots, isLoading: slotsLoading } = useQuery({
    queryKey: ['slots'],
    queryFn: () => api<Slot[]>('/api/scheduling/slots'),
    enabled: pickerOpen,
  });

  const { data: waitlist } = useQuery({
    queryKey: ['my-waitlist'],
    queryFn: () => api<WaitlistEntry[]>('/api/scheduling/waitlist'),
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const cancel = useMutation({
    mutationFn: (id: string) => api(`/api/scheduling/lessons/${id}/cancel`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const book = useMutation({
    mutationFn: (startsAt: string) => api('/api/scheduling/book', { body: { startsAt } }),
    onSuccess: () => {
      setPickerOpen(false);
      invalidate();
    },
  });

  const reschedule = useMutation({
    mutationFn: ({ id, startsAt }: { id: string; startsAt: string }) =>
      api(`/api/scheduling/lessons/${id}/reschedule`, { body: { startsAt } }),
    onSuccess: () => {
      setPickerOpen(false);
      invalidate();
    },
  });

  const joinWaitlist = useMutation({
    mutationFn: () => api('/api/scheduling/waitlist', { body: {} }),
    onSuccess: invalidate,
  });

  const claim = useMutation({
    mutationFn: (id: string) => api(`/api/scheduling/waitlist/${id}/claim`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  function onCancel(lesson: Lesson) {
    if (window.confirm('Cancel this lesson?')) cancel.mutate(lesson.id);
  }

  function onPickSlot(slot: Slot) {
    const when = fmtDateTime(slot.startsAt);
    if (rescheduleId) {
      if (window.confirm(`Move your lesson to ${when}?`)) reschedule.mutate({ id: rescheduleId, startsAt: slot.startsAt });
    } else {
      if (window.confirm(`Book a lesson at ${when}?`)) book.mutate(slot.startsAt);
    }
  }

  const upcoming = (lessons ?? []).filter((l) => l.status === 'scheduled' || l.status === 'completed');
  const offered = (waitlist ?? []).filter((e) => e.status === 'offered');
  const waiting = (waitlist ?? []).filter((e) => e.status === 'waiting');

  const slotsByDay = (slots ?? []).reduce<Record<string, Slot[]>>((acc, s) => {
    const day = new Date(s.startsAt).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    (acc[day] ??= []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Lessons</h1>
        <Button
          onClick={() => {
            setRescheduleId(null);
            setPickerOpen(true);
          }}
        >
          + Book a lesson
        </Button>
      </div>

      {/* live room occupied (even outside the scheduled window) */}
      <LiveNowBanner role="student" />

      {/* G7 · waitlist offer — first come, first served */}
      {offered.map((e) => (
        <Card key={e.id} className="border-amber-300 bg-amber-50">
          <p className="text-sm font-semibold text-amber-900">🎉 A slot opened up for you!</p>
          <p className="mt-1 text-xs text-amber-800">First come, first served — claim it before someone else does.</p>
          <Button className="mt-3" onClick={() => claim.mutate(e.id)} disabled={claim.isPending}>
            Claim the slot
          </Button>
          <ErrorText error={claim.error} />
        </Card>
      ))}

      {isLoading ? (
        <Spinner />
      ) : upcoming.length === 0 ? (
        <EmptyState
          title="No upcoming lessons"
          hint="Book a slot from your coach's availability, or join the waitlist."
          action={
            <Button
              onClick={() => {
                setRescheduleId(null);
                setPickerOpen(true);
              }}
            >
              Book a lesson
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {upcoming.map((l) => (
            <Card key={l.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">
                    {fmtDateTime(l.startsAt)} – {fmtTime(l.endsAt)}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">with {l.coachName ?? 'your coach'}</p>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {l.status === 'scheduled' &&
                    new Date(l.startsAt) <= new Date() &&
                    new Date(l.endsAt) >= new Date() && <Tag tone="brand">now</Tag>}
                  {l.isTrial && <Tag tone="amber">Trial</Tag>}
                  {l.status === 'completed' && <Tag tone="green">completed</Tag>}
                </div>
              </div>
              {l.status === 'scheduled' && (
                <div className="mt-2">
                  <Link to={`/app/live/${l.id}`} className="text-sm font-medium text-brand-700 underline">
                    🎥 Join live room
                  </Link>
                </div>
              )}
              {l.sharedSummary && (
                <p className="mt-2 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-900">
                  <span className="font-semibold">What we worked on:</span> {l.sharedSummary}
                </p>
              )}
              {l.status === 'scheduled' && (
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="secondary"
                    className="px-3 py-1.5 text-xs"
                    onClick={() => {
                      setRescheduleId(l.id);
                      setPickerOpen(true);
                    }}
                  >
                    Reschedule
                  </Button>
                  <Button variant="danger" className="px-3 py-1.5 text-xs" onClick={() => void onCancel(l)} disabled={cancel.isPending}>
                    Cancel
                  </Button>
                </div>
              )}
            </Card>
          ))}
          <ErrorText error={cancel.error ?? reschedule.error} />
        </div>
      )}

      {/* G7 · waitlist */}
      <Card title="Waitlist">
        {waiting.length > 0 ? (
          <p className="text-sm text-slate-700">You're on the waitlist — we'll notify you when a slot opens. ⏳</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">No slot that fits? Join the waitlist and grab cancellations first.</p>
            <Button variant="secondary" onClick={() => joinWaitlist.mutate()} disabled={joinWaitlist.isPending}>
              Join the waitlist
            </Button>
          </div>
        )}
        <ErrorText error={joinWaitlist.error} />
      </Card>

      <IcsCard />

      {/* G2 · slot picker (booking + rescheduling) */}
      <Modal
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setRescheduleId(null);
        }}
        title={rescheduleId ? 'Pick a new time' : 'Book a lesson'}
      >
        {slotsLoading ? (
          <Spinner />
        ) : !slots || slots.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">No open slots in the next 3 weeks.</p>
            <Button
              variant="secondary"
              onClick={() => {
                joinWaitlist.mutate();
                setPickerOpen(false);
              }}
            >
              Join the waitlist instead
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(slotsByDay).map(([day, daySlots]) => (
              <div key={day}>
                <p className="mb-1.5 text-xs font-semibold tracking-wide text-slate-500 uppercase">{day}</p>
                <div className="flex flex-wrap gap-1.5">
                  {daySlots.map((s) => (
                    <button
                      key={s.startsAt}
                      onClick={() => onPickSlot(s)}
                      disabled={book.isPending || reschedule.isPending}
                      className="rounded-lg border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-100 disabled:opacity-50"
                    >
                      {fmtTime(s.startsAt)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <ErrorText error={book.error ?? reschedule.error} />
          </div>
        )}
      </Modal>
    </div>
  );
}
